import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth0 } from '@auth0/auth0-react';
import { ApiError, createApiErrorFromResponse } from '../utils/apiError';

// API base URL: Use environment variable or default based on NODE_ENV
// In development, use full localhost URL to bypass proxy issues
// In production, use full URL
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  (process.env.NODE_ENV === 'development'
    ? 'http://localhost:5001' // Full URL to bypass proxy
    : 'https://api-eternavaults.onrender.com');

// Custom hook for authenticated API calls
// - Always attaches Auth0 bearer token
// - Throws ApiError on non-OK responses
// - Preserves AbortError so callers can handle cancellations
export const useAuthenticatedFetch = () => {
  const { getAccessTokenSilently } = useAuth0();

  return React.useCallback(async (url, options = {}) => {
    const token = await getAccessTokenSilently({
      audience: 'https://api.eternavaults.com',
    });

    // Allow caller to provide their own AbortController.signal
    const { signal, headers, ...rest } = options;

    // Check if body is FormData - if so, don't set Content-Type (browser will set it with boundary)
    const isFormData = rest.body instanceof FormData;

    // Build headers object
    const requestHeaders = {
      Authorization: `Bearer ${token}`,
      ...headers,
    };

    // Only set Content-Type for non-FormData requests
    if (!isFormData && !headers?.['Content-Type']) {
      requestHeaders['Content-Type'] = 'application/json';
    }

    try {
      const fullUrl = `${API_BASE_URL}${url}`;
      const response = await fetch(fullUrl, {
        ...rest,
        signal,
        headers: requestHeaders,
      });

      if (!response.ok) {
        throw await createApiErrorFromResponse(response);
      }

      // Handle 204 No Content
      if (response.status === 204) {
        return null;
      }

      return response.json();
    } catch (error) {
      // Let AbortError bubble up so effects can ignore it
      if (error.name === 'AbortError') {
        throw error;
      }

      // Normalize unknown errors into ApiError
      if (!(error instanceof ApiError)) {
        throw new ApiError(error.message || 'Network error', { status: 0 });
      }

      throw error;
    }
  }, [getAccessTokenSilently]);
};

// Dashboard data hooks
export const useDashboardData = (memberId) => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['dashboard', memberId],
    queryFn: () => authenticatedFetch(`/api/dashboard/${memberId}`),
    enabled: !!memberId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

export const useMemberData = (memberId) => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['member', memberId],
    queryFn: () => authenticatedFetch(`/api/members/${memberId}`),
    enabled: !!memberId,
    staleTime: 30 * 1000, // 30 seconds - shorter for profile updates
  });
};

export const useCurrentUser = () => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['currentUser'],
    queryFn: () => authenticatedFetch('/api/members/me'),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const useFamilyMembers = () => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['familyMembers'],
    queryFn: () => authenticatedFetch('/api/families/my'),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useStorageUsage = () => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['storageUsage'],
    queryFn: () => authenticatedFetch('/api/dashboard/storage-usage'),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

// Access context (authority + capabilities + limits)
export const useAccessContext = () => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['accessContext'],
    queryFn: () => authenticatedFetch('/api/access/context'),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Soft succession: when incapacity is verified (no full authority transfer), CP can see admin dashboard (locked)
// Pass memberId (dashboard URL member) so shared-login families use the correct member for CP check.
export const useSoftSuccessionContext = (memberId) => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['softSuccessionContext', memberId],
    queryFn: () => {
      const q = memberId ? `?memberId=${encodeURIComponent(memberId)}` : '';
      return authenticatedFetch(`/api/succession/soft-succession-context${q}`);
    },
    enabled: !!memberId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

// Admin hooks
export const useAdminStats = () => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['adminStats'],
    queryFn: () => authenticatedFetch('/api/admin/stats'),
    staleTime: 1 * 60 * 1000, // 1 minute
  });
};

export const useSubscriptionStatus = () => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['subscriptionStatus'],
    queryFn: () => authenticatedFetch('/api/subscription/status'),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const usePlanInfo = () => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['planInfo'],
    queryFn: () => authenticatedFetch('/api/subscription/plan-info'),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Message Center hooks
export const useNotifications = () => {
  const authenticatedFetch = useAuthenticatedFetch();

  return useQuery({
    queryKey: ['notifications'],
    queryFn: () => authenticatedFetch('/api/notifications'),
    staleTime: 1 * 60 * 1000, // 1 minute
  });
};

// Mutation hooks for data updates
export const useArchiveNotification = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notificationId) =>
      authenticatedFetch(`/api/notifications/${notificationId}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries(['notifications']);
    },
  });
};

export const useRefreshData = () => {
  const queryClient = useQueryClient();

  return (memberId) => {
    // Invalidate all queries
    queryClient.invalidateQueries();

    // Specifically invalidate member data if memberId is provided
    if (memberId) {
      queryClient.invalidateQueries(['member', memberId]);
    }
  };
};
